/**
 * 재접속 대조 — 세션 스트림이 끊겼다 다시 붙을 때, 공백 중 일어난 일을 사실로 맞춘다.
 *
 * SM 레플리카가 바뀌거나(재구성) BFF 가 재배포되는 동안 실행은 계속 돌고 끝나기도 한다. 그동안의 세션 이벤트는
 * 재구독이 (세대, seq) 커서로 받아 오지만, 이벤트가 못 오는 경우(로그가 비워졌거나 세션이 다른 삶을 살았거나)의
 * 마지막 안전망이 이 대조다. 규칙:
 * - **세션 응답이 정본**이다. 스토어의 실행이 세션의 시뮬레이션 집합에 **진행 중** 상태로 있으면 그대로 둔다 — 그 뒤
 *   상태는 이벤트가 싣는다(재접속 때 무조건 이력으로 덮으면 실행 중인 세션을 이력의 옛 상태로 되돌린다).
 * - 집합에 **없거나 종결 상태로만 남아 있으면**(스냅샷은 정리 전의 COMPLETED·STOPPED 도 보인다) 끝난 것이다. 이력
 *   (엄격 조회)이 마감됐으면 그 판정으로 닫고, 아직 마감 전이면 «상태 모름» 으로 두고 이벤트·이력 갱신을 기다린다 —
 *   아는 척하지 않는다.
 * - 조회 자체의 실패는 던진다(재수립 루프가 백오프로 다시 온다).
 * - 서버가 세션을 모른다(NotFound — 세션 소실)는 «그 세션에 실행이 하나도 없다» 와 같은 사실이다 — 같은 대조를 빈 세션으로
 *   태운다(reconcileRunAfterSessionLoss). 다시 올 재수립 루프가 없으므로 이력 조회마저 실패하면 «상태 모름» 으로 두고 기록
 *   재조회에 맡긴다. 세션을 접기만 하고 대조하지 않으면 공백 중 끝난 실행이 RUNNING 으로 남는다(스테이지 2026-10-15).
 */
import { useSimulationStore } from '@/app/stores/simulationStore';
import { getSimulationHistoryStrict } from '@/app/services/pm/projectManagerService';
import { smSimInProgress } from '@/app/services/sm/taskStatus';
import { SessionState } from '@/stubs/sm/simulation_manager_pb';
import { entryIsFinal, entryParticipantVerdict, type SimulationEntry } from '@/types/project';
import { runOnServer, TERMINAL_SIM_STATUSES, type SimSession, type SimStatus } from '@/types/simulation';

const TERMINAL: ReadonlySet<SimStatus> = new Set(TERMINAL_SIM_STATUSES);

/** 대조의 결과 — 로그·시험용. 화면은 스토어를 본다 */
export type ReconcileOutcome =
  | { kind: 'no-run' }          // 스토어에 실행이 없거나 이미 종결, 또는 조회 중에 다른 실행으로 바뀜
  | { kind: 'present' }         // 세션이 아직 진행 중으로 들고 있다 — 이벤트에 맡긴다
  | { kind: 'closed'; status: SimStatus }   // 세션에 없고 이력이 마감 → 기록으로 닫음
  | { kind: 'unknown' };        // 세션에 없는데 이력이 아직 — 상태 모름

/** «상태 모름» 뒤 이력 재조회 간격 — 세션에서 사라진 실행의 기록이 마감되는 데 걸리는 보통의 시간(저널 소비 수 초)을
 *  충분히 덮는다. 다 지나도 마감 전이면 «상태 모름» 으로 남긴다(날조하지 않는다). */
export const UNKNOWN_RECHECK_DELAYS_MS: readonly number[] = [5_000, 10_000, 20_000, 30_000, 30_000, 30_000];

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * 세션 응답을 정본으로 스토어의 실행을 대조한다. 세션 조회는 호출자가 한다(이 모듈은 SM 클라이언트를 모른다).
 * @param signal 이 대조가 속한 재수립의 중단 신호 — 기다리는 사이 재수립 세대가 바뀌었으면(구독 변경) 옛 세션
 *   응답으로 지금 스토어를 만지지 않는다(새 세대가 실행을 다시 확인했는데 옛 조회가 뒤늦게 덮는 것).
 */
export async function reconcileRunWithSession(state: SessionState, signal?: AbortSignal): Promise<ReconcileOutcome> {
  const store = useSimulationStore.getState();
  const session = store.simSession;
  // 서버에 실행이 없는 세션(데모 — `runOnServer`)은 대조할 실행이 없다
  if (!session || !runOnServer(session) || TERMINAL.has(session.status)) return { kind: 'no-run' };
  const snapshot = (state.simulations ?? []).find((s) => s.simId === session.simId);
  if (snapshot && smSimInProgress(snapshot.status)) return { kind: 'present' };

  const entry = await getSimulationHistoryStrict(session.simId);
  if (signal?.aborted) return { kind: 'no-run' }; // 이 재수립은 끝났다 — 다음 세대의 대조가 다시 본다
  // 기다리는 사이 사용자가 다른 실행으로 바꿨거나 세션을 비웠거나, 늦게 온 이벤트가 이 실행을 이미 종결시켰으면
  // 옛 실행·옛 사실 기준으로 지금 스토어를 만지지 않는다
  const current = useSimulationStore.getState().simSession;
  if (!current || current.simId !== session.simId || TERMINAL.has(current.status)) return { kind: 'no-run' };
  if (entry && entryIsFinal(entry)) return closeRunFromRecord(current, entry);
  return markUnknownUntilRecorded(current, '세션에 없는데 이력이 아직');
}

/**
 * 세션 소실(서버가 세션을 모른다고 확정 — NotFound) 뒤의 대조. 세션이 없다는 것은 그 세션에 실행이 하나도 없다는 것과 같은
 * 사실이라 {@link reconcileRunWithSession} 을 빈 세션으로 태운다. 재접속과 달리 다시 올 재수립 루프가 없으므로 이력 조회마저
 * 실패하면 던지지 않고 «상태 모름» 으로 두고 기록 재조회에 맡긴다.
 */
export async function reconcileRunAfterSessionLoss(sessionId: string): Promise<ReconcileOutcome> {
  const simId = useSimulationStore.getState().simSession?.simId;
  try {
    return await reconcileRunWithSession(new SessionState({ sessionId, simulations: [] }));
  } catch (err) {
    console.warn('[sessionReconcile] 세션 소실 뒤 이력 조회 실패 — 기록 재조회에 맡긴다:', err);
    const current = useSimulationStore.getState().simSession;
    // 실패를 기다리는 사이 실행이 바뀌었으면(다른 실행으로 전환·새 실행 시작) 지금 스토어를 만지지 않는다 —
    // 성공 경로의 재확인(위)과 같은 규칙. 옛 세션의 실패로 새 실행을 «상태 모름» 으로 덮지 않는다
    if (!current || current.simId !== simId || TERMINAL.has(current.status)) return { kind: 'no-run' };
    return markUnknownUntilRecorded(current, '세션 소실 뒤 이력을 아직 못 읽음');
  }
}

/** 끝난 것은 알지만 판정은 모른다 — «상태 모름» 으로 두고, 기록이 마감되면 그것으로 닫는다(날조하지 않는다) */
function markUnknownUntilRecorded(session: SimSession, why: string): ReconcileOutcome {
  const store = useSimulationStore.getState();
  for (const model of Object.values(session.models)) {
    if (TERMINAL.has(model.status) || model.status === 'unknown') continue;
    store.updateModelByTaskId(model.taskId, { status: 'unknown' });
  }
  store.setSessionStatus('unknown');
  console.warn(`[sessionReconcile] ${why} — 상태 모름: sim=${session.simId}`);
  // 이벤트가 끝내 못 오는 경우(옛 레플리카와 함께 사라짐)의 안전망 — 기록이 마감되면 그것으로 닫는다.
  // 스트림 재수립과 병행하며, 실행이 바뀌거나 이벤트가 먼저 닫으면 그만둔다.
  void recheckRecordUntilFinal(session.simId);
  return { kind: 'unknown' };
}

/** 기록(마감된 이력)으로 실행을 닫는다 — 참가자 판정·사유는 기록의 것, 모르는 참가자는 «상태 모름» 그대로 */
function closeRunFromRecord(session: SimSession, entry: SimulationEntry): ReconcileOutcome {
  const store = useSimulationStore.getState();
  const status = entry.status;
  const reasonByTask = new Map((entry.participants ?? []).map((p) => [p.taskId, p.detail ?? '']));
  // 종료 시각은 기록의 것 — 지금 시각을 쓰면 실제 종료부터 기록 마감·재접속까지의 지연이 소요 시간에 더해진다.
  // 기록에 없으면(구 이력) 지금 시각 — 모르는 것을 지어내지 않되 종료는 찍어야 경과 시계가 멈춘다
  const recordEnd = entry.endedAt ? Date.parse(entry.endedAt) : Number.NaN;
  const endTime = Number.isFinite(recordEnd) ? recordEnd : Date.now();
  for (const model of Object.values(session.models)) {
    if (TERMINAL.has(model.status)) continue;
    // 참가자 판정이 있는 기록에서 이 참가자의 판정을 모르면 실행 판정으로 메우지 않는다 — 모름은 모름
    const verdict = entryParticipantVerdict(entry, model.taskId) ?? 'unknown';
    const reason = reasonByTask.get(model.taskId);
    store.updateModelByTaskId(model.taskId, {
      status: verdict,
      endTime: model.endTime ?? endTime,
      ...(reason && reason !== model.error ? { error: reason } : {}),
    });
  }
  store.setSessionStatus(status);
  console.warn(`[sessionReconcile] 세션에 없는 실행을 기록으로 닫음: sim=${session.simId} → ${status}`);
  return { kind: 'closed', status };
}

// 실행별 재조회 체인은 하나 — 끊김이 거듭돼 대조가 «상태 모름» 을 여러 번 내도 체인을 겹쳐 돌리지 않는다
const recheckInFlight = new Set<string>();

/** «상태 모름» 뒤 — 기록이 마감될 때까지 간격표대로 재조회해 마감되면 닫는다. 다 지나면 «상태 모름» 유지.
 *  같은 실행의 체인이 이미 돌고 있으면 그것에 맡긴다 */
export async function recheckRecordUntilFinal(simId: string): Promise<ReconcileOutcome> {
  if (recheckInFlight.has(simId)) return { kind: 'unknown' };
  recheckInFlight.add(simId);
  try {
    for (const delay of UNKNOWN_RECHECK_DELAYS_MS) {
      await sleep(delay);
      const session = useSimulationStore.getState().simSession;
      if (!session || session.simId !== simId || TERMINAL.has(session.status)) return { kind: 'no-run' };
      let entry: SimulationEntry | null = null;
      try {
        entry = await getSimulationHistoryStrict(simId);
      } catch (err) {
        console.warn('[sessionReconcile] 이력 재조회 실패 — 다음 간격에 다시:', simId, err);
        continue;
      }
      const current = useSimulationStore.getState().simSession;
      if (!current || current.simId !== simId || TERMINAL.has(current.status)) return { kind: 'no-run' };
      if (entry && entryIsFinal(entry)) return closeRunFromRecord(current, entry);
    }
    console.warn(`[sessionReconcile] 기록이 마감되지 않음 — 상태 모름 유지: sim=${simId}`);
    return { kind: 'unknown' };
  } finally {
    recheckInFlight.delete(simId);
  }
}
